import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ProgressStepper from './components/ProgressStepper';
import Step1 from './components/steps/Step1.jsx';
import Step2 from './components/steps/Step2.jsx';
import Step3 from './components/steps/Step3.jsx';
import Step4 from './components/steps/Step4.jsx';
import Step5 from './components/steps/Step5.jsx';

import {
  QUIZ_STEPS,
  STORAGE_KEYS,
  QUIZ_OPTIONS_DATA,
  getQuizOptionsData,
} from './constants';

import { useQuizSelections } from './hooks/useLocalStorage';
import { preloadNextStepImages, preloadVideos } from './utils/imagePreloader';
import { initTracking } from './utils/tracking';
import {
  trackStyleSelected,
  trackStep,
  trackClickBack,
} from './utils/analytics';
import { useSteps } from './hooks/useSteps';
import { useGrowthBookValues } from './contexts/GrowthBookContext';
import LogoSvg from './assets/Logo.svg';

const STEPS_COMPONENTS = {
  step1: Step1,
  step2: Step2,
  step3: Step3,
  step4: Step4,
  step5: Step5,
};

const validateStepData = (step, answer) => {
  switch (step) {
    case 'step1':
      return !!answer?.ethnicity;
    case 'step2':
      return !!answer?.personality;
    case 'step3':
      return !!answer?.bodyType;
    case 'step4':
      return !!(answer?.hairStyle && answer?.eyeColor);
    case 'step5':
      return !!(answer?.voice && answer?.relationship && answer?.age);
    default:
      return true;
  }
};

function Home() {
  const [currentStep, setCurrentStep] = useState(0);
  const [displayedStep, setDisplayedStep] = useState(0);
  const [transitionState, setTransitionState] = useState('idle');

  const location = useLocation();
  const navigate = useNavigate();
  const [selections, setSelections] = useQuizSelections();
  const pendingStepRef = useRef(null);

  const { t } = useTranslation();
  const { getActiveSteps } = useSteps();
  const { isReady: isGBReady } = useGrowthBookValues();

  const activeSteps = useMemo(
    () => getActiveSteps(QUIZ_STEPS),
    [getActiveSteps],
  );

  // `summary` is a marker — when it becomes the current step, redirect to /summary.
  useEffect(() => {
    if (activeSteps[currentStep] === 'summary') {
      navigate('summary', { replace: true });
    }
  }, [currentStep, activeSteps, navigate]);

  // Preload videos for the next step while the user interacts with the current one.
  useEffect(() => {
    const STEP_TO_VIDEO_DATA = {
      step1: 'personality',
      step2: 'bodyType',
    };

    const currentStepName = activeSteps[displayedStep];
    const nextDataKey = STEP_TO_VIDEO_DATA[currentStepName];
    if (!nextDataKey) return;

    const nextStepItems = getQuizOptionsData(nextDataKey);
    const videoUrls = nextStepItems.map((item) => item.video).filter(Boolean);

    if (videoUrls.length > 0) {
      preloadVideos(videoUrls);
    }
  }, [displayedStep, activeSteps]);

  const transitionToStep = useCallback(
    (newStep) => {
      if (transitionState !== 'idle') return;

      pendingStepRef.current = newStep;
      setTransitionState('fade-out');
    },
    [transitionState],
  );

  const handleAnswer = useCallback(
    (step, answer) => {
      if (!validateStepData(step, answer)) return;

      // step1 is the first screen → `style_selected`; step2..step5 → step_2..step_5.
      if (step === 'step1') {
        trackStyleSelected(answer);
      } else {
        const stepNumber = parseInt(step.replace('step', ''), 10);
        trackStep(stepNumber, answer);
      }

      const existing = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.AI_TRANS_SELECTIONS) || '{}',
      );

      const updated = { ...existing, [step]: answer };

      localStorage.setItem(
        STORAGE_KEYS.AI_TRANS_SELECTIONS,
        JSON.stringify(updated),
      );

      setSelections(updated);

      setTimeout(() => {
        preloadNextStepImages(step, QUIZ_OPTIONS_DATA);
      }, 100);

      transitionToStep(currentStep + 1);
    },
    [setSelections, currentStep, transitionToStep],
  );

  const handlePrevious = useCallback(() => {
    if (transitionState !== 'idle' || currentStep === 0) return;
    trackClickBack({ from_step: currentStep + 1, to_step: currentStep });
    transitionToStep(currentStep - 1);
  }, [transitionState, currentStep, transitionToStep]);

  const resetQuiz = useCallback(() => {
    setCurrentStep(0);
    setDisplayedStep(0);
    setSelections({});
    setTransitionState('idle');
    localStorage.removeItem(STORAGE_KEYS.AI_TRANS_SELECTIONS);
  }, [setSelections]);

  useEffect(() => {
    if (transitionState === 'fade-out') {
      const timer = setTimeout(() => {
        setCurrentStep(pendingStepRef.current);
        setDisplayedStep(pendingStepRef.current);
        setTransitionState('fade-in');
      }, 300);
      return () => clearTimeout(timer);
    }

    if (transitionState === 'fade-in') {
      const timer = setTimeout(() => {
        setTransitionState('idle');
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [transitionState]);

  useEffect(() => {
    initTracking();
  }, []);

  const visibleSteps = useMemo(
    () => activeSteps.filter((s) => s !== 'summary'),
    [activeSteps],
  );

  const currentStepName = activeSteps[displayedStep];
  const CurrentStepComponent = STEPS_COMPONENTS[currentStepName];
  const isTransitioning = transitionState !== 'idle';

  if (!CurrentStepComponent) {
    if (currentStepName === 'summary') return null;

    return (
      <div className="quizContentWrapper">
        <div className="quizContent">
          <h1>{t('step_not_found')}</h1>
          <p><strong>URL:</strong> {location.pathname}</p>
          <p><strong>Current step:</strong> {currentStep}</p>
          <p><strong>Step name:</strong> {currentStepName}</p>
          <button onClick={resetQuiz}>{t('reset_quiz')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`app ${isTransitioning ? 'no-click' : ''}`}>
      <img
        src={LogoSvg}
        alt="Swipey"
        className="app-logo"
        width={100}
        height={24}
      />

      <ProgressStepper
        currentStep={currentStep}
        totalSteps={visibleSteps.length}
      />

      <div
        className={`step-wrapper ${
          transitionState === 'fade-out'
            ? 'fade-out'
            : transitionState === 'fade-in'
            ? 'fade-in'
            : ''
        }`}
      >
        {isGBReady && (
          <CurrentStepComponent
            onAnswer={handleAnswer}
            selections={selections}
            onPrevious={handlePrevious}
            isTransitioning={isTransitioning}
          />
        )}
      </div>
    </div>
  );
}

export default Home;
